import { BentoItem, HorizontalPillar, ImpactMetric, ObservatoryVoice, SimulatorPreset } from '../types/landing';

export const MANIFESTO_STATEMENTS = [
  "The planetary biosphere does not respond to carbon offsets.",
  "It does not negotiate with net-zero declarations.",
  "It responds exclusively to thermodynamics and kinetic throughput.",
  "Every hectare re-engineered.",
  "Every megaton of basaltic carbon locked into crystal.",
  "Every marine alkalinity buffer deployed.",
  "Every species corridor monitored in real time.",
  "GAIA PULSE engineers autonomous ecological systems at planetary scale.",
  "Zero greenwashing. Absolute empirical telemetry. Permanent equilibrium.",
];

export const PLANETARY_PILLARS: HorizontalPillar[] = [
  {
    id: "pillar-canopy",
    index: "01",
    tag: "AUTONOMOUS REFORESTATION",
    title: "Sovereign Aerial Bio-Swarms",
    subtitle: "High-cadence aerial lidar swarms delivering 120,000 mycorrhizal seed-pellets per hour.",
    description: "Multi-rotor drone clusters guided by multispectral lidar terrain maps. Each capsule contains native symbiotic fungi, moisture-retentive hydrogel, and micro-nutrients calibrated for 94% germination success on degraded terrains.",
    metrics: [
      { label: "PLANTING CADENCE", value: "120K / HR" },
      { label: "GERMINATION RATE", value: "94.2%" },
      { label: "SPECIES DIVERSITY", value: "38 NATIVE" },
    ],
    accentColor: "#00F5A0",
    iconName: "drone-swarm",
  },
  {
    id: "pillar-ocean",
    index: "02",
    tag: "OCEAN ALKALINITY ENHANCEMENT",
    title: "Abyssal Carbon Mineralization",
    subtitle: "Synthetic olivine micro-dispersion accelerating natural oceanic carbon sink kinetics.",
    description: "Deep-water maritime autonomous vessels disperse finely milled non-toxic alkaline minerals into surface currents, neutralizing local oceanic acidification while permanently fixing atmospheric CO2 as dissolved bicarbonate.",
    metrics: [
      { label: "ALKALINITY FLUX", value: "+14.8 MEQ" },
      { label: "CORAL CALCIFICATION", value: "+38%" },
      { label: "STORAGE PERMANENCE", value: "> 10,000 YRS" },
    ],
    accentColor: "#38BDF8",
    iconName: "ocean-alkalinity",
  },
  {
    id: "pillar-mineralization",
    index: "03",
    tag: "DIRECT AIR GEOTHERMAL LOCK",
    title: "Basaltic Crystal Sequestration",
    subtitle: "Closed-loop geothermal scrubbing arrays injecting dissolved CO2 into basaltic bedrock.",
    description: "Powered by 100% supercritical geothermal baseload energy. Trapped CO2 reacts with deep subterranean basalt formations to precipitate into solid carbonate rock within 18 months, guaranteeing irreversible containment.",
    metrics: [
      { label: "CAPTURE PURITY", value: "99.8%" },
      { label: "PRECIPITATION SPEED", value: "< 18 MOS" },
      { label: "GRID PENALTY", value: "0.0% FOSSIL" },
    ],
    accentColor: "#F59E0B",
    iconName: "mineralization",
  },
  {
    id: "pillar-mycelium",
    index: "04",
    tag: "SUBTERRANEAN BIOREMEDIATION",
    title: "Engineered Mycelial Networks",
    subtitle: "Subterranean fungal biosensors and bio-filters restoring contaminated watersheds.",
    description: "Macro-fungal hyphal lattices genetically primed to hyper-accumulate industrial heavy metals and break down organophosphates, returning barren industrial soil to living agricultural humus.",
    metrics: [
      { label: "HYDROCARBON BREAKDOWN", value: "98.7%" },
      { label: "SOIL MICROBIOME GAIN", value: "4.8X" },
      { label: "RUNOFF RETENTION", value: "+65%" },
    ],
    accentColor: "#10B981",
    iconName: "mycelium",
  },
];

export const BENTO_ITEMS: BentoItem[] = [
  {
    id: "bento-telemetry",
    colSpan: "lg:col-span-8",
    tag: "PLANETARY SENSING FABRIC",
    title: "Satellite Multispectral Lidar & Canopy Tomography",
    description: "Sub-meter resolution orbital synthetic aperture radar fused with edge AI drones to compute biomass density, sap flow, and moisture stress across entire continental biomes in real time.",
    technicalSpec: "SAR_LIDAR_SPECTRAL // 0.8M RESOLUTION // REALTIME",
    metric: { label: "BIOMASS AUDIT ACCURACY", value: "99.8", unit: "%" },
    badge: "GLOBAL REALTIME ORBIT",
    glowColor: "emerald",
  },
  {
    id: "bento-ledger",
    colSpan: "lg:col-span-4",
    tag: "CRYPTOGRAPHIC PROOF",
    title: "Immutable Carbon Proof Ledger",
    description: "Every metric ton of carbon mineralized is verified by cryptographic multi-party hardware attestations, eliminating phantom carbon credits.",
    technicalSpec: "ZERO_KNOWLEDGE // PROOF_OF_DRAWDOWN",
    metric: { label: "PHANTOM RISK", value: "0.00", unit: "%" },
    badge: "AUDITED",
    glowColor: "solar",
  },
  {
    id: "bento-swarms",
    colSpan: "lg:col-span-4",
    tag: "FLEET ORCHESTRATION",
    title: "Swarm Kinetics & Mesh Telemetry",
    description: "Over 14,000 autonomous bio-drones coordinate via peer-to-peer RF mesh, executing millimeter-precise seed pellet ballistic injection even in dense gale conditions.",
    technicalSpec: "MESH_LATENCY // 1.2MS // ADAPTIVE_FORMATION",
    metric: { label: "MISSION READINESS", value: "99.4", unit: "%" },
    glowColor: "ozone",
  },
  {
    id: "bento-sensors",
    colSpan: "lg:col-span-4",
    tag: "RHIZOSPHERE TELEMETRY",
    title: "Subterranean Microbial Acoustic Array",
    description: "Acoustic micro-transducers buried across restoration sectors listen to subterranean earthworm activity and root growth micro-fractures to index soil vitality.",
    technicalSpec: "BIO_ACOUSTIC // 10HZ-40KHZ SPECTRAL DENSITY",
    metric: { label: "SENSOR NODES", value: "240", unit: "K" },
    glowColor: "emerald",
  },
  {
    id: "bento-energy",
    colSpan: "lg:col-span-4",
    tag: "ZERO-LOSS MICROGRIDS",
    title: "Geothermal & Solar Baseload Coupling",
    description: "All direct-air capture arrays and bio-laboratories run exclusively on off-grid geothermal steam taps and agrivoltaic dual-axis solar trackers.",
    technicalSpec: "CLEAN_POWER_FACTOR // 100% RENEWABLE",
    metric: { label: "CARBON INTENSITY", value: "0.0", unit: "g/kWh" },
    glowColor: "solar",
  },
];

export const SIMULATOR_PRESETS: SimulatorPreset[] = [
  {
    id: "preset-amazon",
    name: "Amazonian Canopy Shield",
    droneSwarmUnits: 18500,
    oceanAlkalinityPct: 40,
    microgridCleanRatio: 98,
    drawdownVelocityGtYr: 1.42,
    projectedTempDeltaC: -0.18,
    canopyAreaRestoredMha: 24.5,
    bioDiversityRecoveryPct: 92.4,
    summary: "Intense aerial mycorrhizal bio-pellet deployment over deforested agricultural corridors in the Tapajós watershed.",
  },
  {
    id: "preset-pacific",
    name: "Oceanic Carbon Sink Accelerator",
    droneSwarmUnits: 8000,
    oceanAlkalinityPct: 95,
    microgridCleanRatio: 100,
    drawdownVelocityGtYr: 2.85,
    projectedTempDeltaC: -0.32,
    canopyAreaRestoredMha: 11.2,
    bioDiversityRecoveryPct: 88.6,
    summary: "Large-scale autonomous marine dispersal of micronized olivine across equatorial Pacific upwelling gyres.",
  },
  {
    id: "preset-boreal",
    name: "Subarctic Permafrost Stabilization",
    droneSwarmUnits: 32000,
    oceanAlkalinityPct: 25,
    microgridCleanRatio: 95,
    drawdownVelocityGtYr: 2.15,
    projectedTempDeltaC: -0.27,
    canopyAreaRestoredMha: 48.0,
    bioDiversityRecoveryPct: 96.1,
    summary: "Extensive larch and birch reforestation paired with geothermal basalt injection across Siberian and Yukon margins.",
  },
];

export const IMPACT_METRICS: ImpactMetric[] = [
  {
    title: "Carbon Fixation Permanence",
    category: "PERMANENCE",
    gaiaValue: "10,000+",
    conventionalValue: "30-50",
    unit: "yrs",
    advantagePercentage: 20000,
    highlight: "Basaltic mineralization turns CO2 into solid stone within 18 months, immune to forest fires.",
  },
  {
    title: "Reforestation Cadence",
    category: "VELOCITY",
    gaiaValue: "120,000",
    conventionalValue: "800",
    unit: "seeds/hr",
    advantagePercentage: 14900,
    highlight: "Autonomous multi-rotor swarms navigate steep topography inaccessible to human crews.",
  },
  {
    title: "Measurement Precision",
    category: "VERIFIABILITY",
    gaiaValue: "99.8%",
    conventionalValue: "64.0%",
    unit: "accuracy",
    advantagePercentage: 56,
    highlight: "Satellite lidar combined with ground rhizosphere acoustic sensors eliminates estimated guesswork.",
  },
  {
    title: "Carbon Footprint of Infrastructure",
    category: "EFFICIENCY",
    gaiaValue: "0.00",
    conventionalValue: "142.5",
    unit: "kg/t CO2",
    advantagePercentage: 100,
    highlight: "Closed-loop geothermal power ensures zero fossil fuel parasitic penalty across all facilities.",
  },
];

export const OBSERVATORY_VOICES: ObservatoryVoice[] = [
  {
    id: "obs-1",
    quote: "Deploying autonomous bio-swarms in the Tapajós river basin restored 180,000 hectares of high-canopy indigenous forest in 14 months. The bio-acoustic recovery curves are unprecedented in conservation history.",
    author: "Dr. Maya Arisawa",
    role: "Chief Ecosystem Ecologist",
    station: "Tapajós Biosphere Laboratory",
    region: "Manaus, Brazil",
    telemetryStamp: "STATION TAPAJOS // LAT -3.04 S",
  },
  {
    id: "obs-2",
    quote: "By accelerating natural olivine weathering in deep currents, we recorded a 42% increase in micro-calcifier plankton density without modifying ambient seawater pH. This is physics applied directly to oceanic revival.",
    author: "Prof. Alistair Thorne",
    role: "Director of Marine Geochemistry",
    station: "Pacific Gyre Research Platform",
    region: "Coral Sea Node",
    telemetryStamp: "VESSEL AEGIS-04 // CORAL REEF TRANSECT",
  },
  {
    id: "obs-3",
    quote: "Direct mineral crystallization in subterranean basalt gives us what paper certificates never could: permanent, physical, irreversible carbon containment that outlasts civilizations.",
    author: "Dr. Freja Lindqvist",
    role: "Principal Geothermal Geophysicist",
    station: "Hekla Deep Basin Facility",
    region: "Reykjavik, Iceland",
    telemetryStamp: "BASALT ARRAY 09 // 1.8KM DEPTH",
  },
];

export const FOOTER_DATA = {
  brandName: "GAIA PULSE",
  tagline: "Autonomous Ecological Architecture // Planetary Equilibrium Engine",
  disclaimer: "IMPORTANT NOTICE & DISCLAIMER: GAIA PULSE is a fictional ecological concept specimen created solely to showcase elite front-end engineering, WebGL 3D kinematics, and bespoke editorial UI design. It is not an active commercial enterprise. All depicted restoration systems, telemetry simulations, and operational metrics are demonstration prototypes.",
  creator: {
    name: "TH157LE",
    telegram: "@TH157LECLAW",
    telegramUrl: "https://t.me/TH157LECLAW",
    email: "support@th157le.ru",
    showcaseDomain: "example.th157le.ru",
    showcaseUrl: "https://example.th157le.ru",
    blogDomain: "thistle.rr.kg",
    blogUrl: "https://thistle.rr.kg",
    statement: "Crafted by TH157LE for discerning digital experiences. Available for bespoke agency-tier web engineering, interactive 3D WebGL applications, and mission-critical UI architecture.",
  },
  biomeClocks: [
    { label: "MANAUS [AMAZONIA]", timeZone: "America/Manaus", biome: "TROPICAL RAINFOREST" },
    { label: "NAIROBI [SAVANNA]", timeZone: "Africa/Nairobi", biome: "SERENGETI CORRIDOR" },
    { label: "SVALBARD [ARCTIC]", timeZone: "Europe/Oslo", biome: "PERMAFROST VAULT" },
    { label: "TOKYO [PACIFIC]", timeZone: "Asia/Tokyo", biome: "BIO-TECH MATRIX" },
  ],
};
